import type { ReactNode } from 'react';
import { Popover } from 'antd';
import { ExternalLink } from 'lucide-react';
import type { OpportunityLinkedIpdVO, OpportunityLinkedIpcVO } from '../api/ipc/opportunity';
import { openProjectScheduleDetail } from '../utils/projectScheduleNav';

function ipdStoryDetailUrl(projectKey: string, workItemId: number): string {
  const key = (projectKey || '').trim();
  return `https://project.feishu.cn/${encodeURIComponent(key)}/story/detail/${workItemId}`;
}

function linkedIpdLabel(item: OpportunityLinkedIpdVO): string {
  const code = (item.projectCode || '').trim();
  const name = (item.name || '').trim();
  if (code && name) return `${code} ${name}`;
  if (code) return code;
  if (name) return name;
  return item.workItemId ? `IPD #${item.workItemId}` : 'IPD';
}

function linkedIpcLabel(item: OpportunityLinkedIpcVO): string {
  const code = (item.code || '').trim();
  const name = (item.name || '').trim();
  if (code && name) return `${code} ${name}`;
  if (name) return name;
  if (code) return code;
  return `IPC #${item.projectId}`;
}

function ipcStatusLabel(status?: string): string {
  const s = (status || '').trim().toLowerCase();
  if (s === 'planning') return '规划中';
  if (s === 'in_progress') return '进行中';
  if (s === 'completed') return '已完成';
  if (s === 'terminated') return '已终止';
  return status?.trim() || '—';
}

type Props = {
  status: string;
  linkedIpds?: OpportunityLinkedIpdVO[];
  linkedIpcs?: OpportunityLinkedIpcVO[];
  /** 视为「无关联可展示」的状态，默认待立项 */
  pendingStatus?: string;
  children: ReactNode;
};

/** 非待立项且有 IPD/IPC 关联时，悬停展示并可跳转 */
export function PlanningLinkStatusHover({
  status,
  linkedIpds,
  linkedIpcs,
  pendingStatus = '待立项',
  children,
}: Props) {
  if (status === pendingStatus) {
    return <>{children}</>;
  }

  const ipds = linkedIpds ?? [];
  const ipcs = linkedIpcs ?? [];
  if (ipds.length === 0 && ipcs.length === 0) {
    return <>{children}</>;
  }

  const content = (
    <div className="opp-status-popover" onClick={e => e.stopPropagation()}>
      <div className="opp-status-popover__section">
        <div className="opp-status-popover__title">关联 IPD</div>
        {ipds.length === 0 ? (
          <div className="opp-status-popover__empty">暂无</div>
        ) : (
          <ul className="opp-status-popover__list">
            {ipds.map(item => {
              const canOpen = !!(item.projectKey || '').trim() && item.workItemId > 0;
              return (
                <li key={`ipd-${item.projectKey}-${item.workItemId}`} className="opp-status-popover__item">
                  <span className="opp-status-popover__text" title={linkedIpdLabel(item)}>
                    {linkedIpdLabel(item)}
                  </span>
                  {canOpen && (
                    <button
                      type="button"
                      className="opp-status-popover__link"
                      title="打开飞书 IPD"
                      onClick={e => {
                        e.stopPropagation();
                        window.open(ipdStoryDetailUrl(item.projectKey, item.workItemId), '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <ExternalLink size={12} aria-hidden />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="opp-status-popover__section">
        <div className="opp-status-popover__title">关联 IPC</div>
        {ipcs.length === 0 ? (
          <div className="opp-status-popover__empty">暂无</div>
        ) : (
          <ul className="opp-status-popover__list">
            {ipcs.map(item => (
              <li key={`ipc-${item.projectId}`} className="opp-status-popover__item">
                <span className="opp-status-popover__text" title={linkedIpcLabel(item)}>
                  {linkedIpcLabel(item)}
                  <span className="opp-status-popover__meta">{ipcStatusLabel(item.status)}</span>
                </span>
                <button
                  type="button"
                  className="opp-status-popover__link"
                  title="打开 IPC 项目详情"
                  onClick={e => {
                    e.stopPropagation();
                    openProjectScheduleDetail(item.projectId, item.currentScheduleId);
                  }}
                >
                  <ExternalLink size={12} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="hover"
      placement="bottomLeft"
      mouseEnterDelay={0.12}
      overlayClassName="opp-status-popover-overlay"
    >
      <span className="opp-status-hit">{children}</span>
    </Popover>
  );
}
